import React, { useState, useRef } from 'react';
import styled from '@emotion/styled';
import { Box, Typography, Button, Alert } from '@mui/material';
import { CloudDownload as CloudDownloadIcon, UploadFile as UploadFileIcon } from '@mui/icons-material';
import { AdminPageContainer, StaticCard } from '../../../components/admin/sharedStyles';
import PageHeader from '../../../components/PageHeader';
import ConfigSubMenu from '../../../components/admin/ConfigSubMenu';
import ConfirmDialog from '../../../components/admin/ConfirmDialog';
import { useDispatch } from 'react-redux';
import { showAlert } from '../../../redux/slices/alerts';
import { backupConfig, restoreConfig } from '../../../redux/slices/config';
import { downloadBlob } from '../../../utils/downloadUtils';
import { toLocalDay } from '../../../utils/dateUtils';

const SectionCard = styled(StaticCard)(({ theme }) => ({
    marginBottom: theme.spacing(3),
}));

const HelperText = styled(Typography)(({ theme }) => ({
    marginBottom: theme.spacing(2),
}));

const ActionsRow = styled(Box)(({ theme }) => ({
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(2),
    flexWrap: 'wrap',
    marginTop: theme.spacing(1),
}));

const FileName = styled(Typography)(({ theme }) => ({
    fontStyle: 'italic',
    color: theme.palette.text.secondary,
}));

const HiddenInput = styled('input')({
    display: 'none',
});

const WarningAlert = styled(Alert)(({ theme }) => ({
    marginBottom: theme.spacing(2),
}));

const BackupRestore = () => {
    const dispatch = useDispatch();
    const fileInputRef = useRef(null);

    const [downloading, setDownloading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [pendingFile, setPendingFile] = useState(null); // { text, name }
    const [confirmOpen, setConfirmOpen] = useState(false);

    const handleBackup = () => {
        setDownloading(true);
        dispatch(backupConfig({
            callback: (res) => {
                setDownloading(false);
                if (!res || !res.ok || !res.data) return;
                downloadBlob(res.data, `pollux-settings-backup-${toLocalDay()}.json`, 'application/json');
            },
        }));
    };

    const handleChooseFile = () => {
        if (fileInputRef.current) fileInputRef.current.click();
    };

    const handleFileChange = (e) => {
        const file = e.target.files?.[0];
        // Allow re-selecting the same file later by clearing the input value.
        e.target.value = '';
        if (!file) return;

        const reader = new FileReader();
        reader.onload = () => {
            setPendingFile({ text: String(reader.result), name: file.name });
            setConfirmOpen(true);
        };
        reader.onerror = () => {
            dispatch(showAlert({ severity: 'error', message: 'Could not read the selected file' }));
        };
        reader.readAsText(file);
    };

    const handleCancelRestore = () => {
        if (saving) return;
        setConfirmOpen(false);
        setPendingFile(null);
    };

    const handleConfirmRestore = () => {
        if (!pendingFile || saving) return;
        setSaving(true);
        dispatch(restoreConfig({
            data: pendingFile.text,
            callback: (res) => {
                setSaving(false);
                if (!res) return;
                dispatch(showAlert({ severity: 'success', message: `Restored ${res.restored ?? 0} settings` }));
                setConfirmOpen(false);
                setPendingFile(null);
            },
        }));
    };

    return (
        <AdminPageContainer>
            <PageHeader
                title="Configuration"
                subtitle="Back up and restore system settings"
            />

            <ConfigSubMenu />

            <SectionCard>
                <Typography variant="h6" gutterBottom>
                    Download a backup
                </Typography>
                <HelperText variant="body2" color="text.secondary">
                    A backup is a single file containing every system setting. It includes sensitive
                    values such as API keys, passwords and credentials, so store the downloaded file
                    somewhere secure and do not share it.
                </HelperText>
                <ActionsRow>
                    <Button
                        variant="contained"
                        color="primary"
                        startIcon={<CloudDownloadIcon />}
                        onClick={handleBackup}
                        disabled={downloading}
                    >
                        {downloading ? 'Preparing…' : 'Download backup'}
                    </Button>
                </ActionsRow>
            </SectionCard>

            <SectionCard>
                <Typography variant="h6" gutterBottom>
                    Restore from a backup
                </Typography>
                <HelperText variant="body2" color="text.secondary">
                    Restoring replaces <strong>all</strong> current settings with the contents of a
                    backup file. This overwrites everything and cannot be undone. Only use a file that
                    was downloaded from this page.
                </HelperText>
                <WarningAlert severity="warning">
                    Choosing a file below will ask you to confirm before anything changes.
                </WarningAlert>
                <ActionsRow>
                    <Button
                        variant="outlined"
                        color="error"
                        startIcon={<UploadFileIcon />}
                        onClick={handleChooseFile}
                    >
                        Choose backup file…
                    </Button>
                    {pendingFile && <FileName variant="body2">{pendingFile.name}</FileName>}
                </ActionsRow>
                <HiddenInput
                    ref={fileInputRef}
                    type="file"
                    accept="application/json,.json"
                    onChange={handleFileChange}
                />
            </SectionCard>

            <ConfirmDialog
                open={confirmOpen}
                onClose={handleCancelRestore}
                onConfirm={handleConfirmRestore}
                title="Restore settings?"
                message={
                    pendingFile
                        ? `Restoring from "${pendingFile.name}" will overwrite ALL current system settings with the contents of this file. This cannot be undone. Continue?`
                        : ''
                }
                confirmLabel={saving ? 'Restoring…' : 'Restore'}
                confirmColor="error"
            />
        </AdminPageContainer>
    );
};

export default BackupRestore;
